import React, { useEffect, useState, useCallback } from 'react';
import { pursuitApi } from '../../api/client';

// Seller pursuit: Award & Debrief. Records the outcome (won/lost/pending/no-bid),
// award value, awardee, and debrief tracking — closing out the pursuit lifecycle.

interface Props { oppId: string; }

const OUTCOMES = [
  { id: 'pending', label: 'Pending', bg: '#f1f5f9', fg: '#475569' },
  { id: 'won', label: 'Won', bg: '#dcfce7', fg: '#166534' },
  { id: 'lost', label: 'Lost', bg: '#fee2e2', fg: '#991b1b' },
  { id: 'no_bid', label: 'No-Bid', bg: '#fef9c3', fg: '#854d0e' },
];

export default function AwardDebriefTab({ oppId }: Props) {
  const [outcome, setOutcome] = useState('pending');
  const [awardAmount, setAwardAmount] = useState('');
  const [awardee, setAwardee] = useState('');
  const [debriefRequested, setDebriefRequested] = useState(false);
  const [debriefNotes, setDebriefNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pursuitApi.getAward(oppId);
      const a = res.award_tracking || {};
      setOutcome(a.outcome || 'pending');
      setAwardAmount(a.award_amount || '');
      setAwardee(a.awardee || '');
      setDebriefRequested(!!a.debrief_requested);
      setDebriefNotes(a.debrief_notes || '');
    } catch (e: any) { setError(e?.message || 'Could not load award tracking.'); }
    setLoading(false);
  }, [oppId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(''); setMsg('');
    try {
      await pursuitApi.saveAward(oppId, {
        outcome, award_amount: awardAmount, awardee,
        debrief_requested: debriefRequested, debrief_notes: debriefNotes,
      });
      setMsg('Saved.');
    } catch (e: any) { setError(e?.message || 'Save failed.'); }
    setSaving(false);
  };

  if (loading) return <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: 18, margin: 0 }}>Award &amp; Debrief</h2>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 16px' }}>
        Record the outcome of this pursuit and track debrief follow-up.
      </p>

      {error && <div role="alert" style={{ color: '#991b1b', background: '#fee2e2', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {msg && <div style={{ color: '#166534', background: '#dcfce7', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{msg}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Outcome</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {OUTCOMES.map(o => (
              <button key={o.id} onClick={() => setOutcome(o.id)}
                style={{ padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: outcome === o.id ? `2px solid ${o.fg}` : '1px solid #e2e8f0',
                  background: outcome === o.id ? o.bg : 'white', color: outcome === o.id ? o.fg : '#64748b' }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <Field label="Award amount (if known)" value={awardAmount} onChange={setAwardAmount} placeholder="e.g. 974855" />
        <Field label="Awardee (if lost / known)" value={awardee} onChange={setAwardee} placeholder="Winning vendor" />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151' }}>
          <input type="checkbox" checked={debriefRequested} onChange={e => setDebriefRequested(e.target.checked)} />
          Debrief requested
        </label>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Debrief notes / lessons learned</div>
          <textarea value={debriefNotes} onChange={e => setDebriefNotes(e.target.value)} rows={5}
            placeholder="What the government said; what to improve next time…"
            style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />
        </div>

        <div>
          <button onClick={save} disabled={saving}
            style={{ padding: '10px 20px', background: saving ? '#93c5fd' : '#10b981', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#374151', fontWeight: 600 }}>
      {label}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 400 }} />
    </label>
  );
}
